import { hideDeferredReviewFields } from "../utils/deferredReviewFields";
import { ProductRecordReview } from "../../product/components/ProductRecordReview";
import type { JsonSchema, UISchemaElement } from "@jsonforms/core";
import caseSchema from "@/fwk.generated/field-catalog/caseDetails.schema.json";
import caseUiSchema from "@/fwk.generated/field-catalog/caseDetails.uischema.json";
import patientSchema from "@/fwk.generated/field-catalog/patient.schema.json";
import patientUiSchema from "@/fwk.generated/field-catalog/patient.uischema.json";
import parentSchema from "@/fwk.generated/field-catalog/parent.schema.json";
import parentUiSchema from "@/fwk.generated/field-catalog/parent.uischema.json";
import reporterSchema from "@/fwk.generated/field-catalog/reporter.schema.json";
import reporterUiSchema from "@/fwk.generated/field-catalog/reporter.uischema.json";
import productSchema from "@/fwk.generated/field-catalog/product.schema.json";
import productUiSchema from "@/fwk.generated/field-catalog/product.uischema.json";
import eventSchema from "@/fwk.generated/field-catalog/event.schema.json";
import eventUiSchema from "@/fwk.generated/field-catalog/event.uischema.json";
import assessmentSchema from "@/fwk.generated/field-catalog/assessment.schema.json";
import assessmentUiSchema from "@/fwk.generated/field-catalog/assessment.uischema.json";
import narrativeSchema from "@/fwk.generated/field-catalog/narrative.schema.json";
import narrativeUiSchema from "@/fwk.generated/field-catalog/narrative.uischema.json";
import summarySchema from "@/fwk.generated/field-catalog/summary.schema.json";
import summaryUiSchema from "@/fwk.generated/field-catalog/summary.uischema.json";
import referencesSchema from "@/fwk.generated/field-catalog/references.schema.json";
import referencesUiSchema from "@/fwk.generated/field-catalog/references.uischema.json";
import type { CaseReviewIconName } from "../components/field_review/CaseReviewIcon";
import type { CaseFieldValues } from "../model/caseReview";
import { catalogCollections, type CollectionPresentation } from "../utils/catalogCollections";
import { INITIAL_FOLLOWUP_TAG, followupVersionTag } from "./caseReviewLabels";
import type { ComponentType } from "react";
import type { RecordTableProps } from "../components/record_review/RecordTable";
import { AssessmentRecordReview } from "../../assessment/components/AssessmentRecordReview";
export { RECORD_ID_PROPERTY } from "./catalogRecordIdentity";

/**
 * Which sections Case Review shows, and their presentation choices.
 *
 * One list, read by the rail, the section menu, the tab strip, the review hook's drafts, the
 * service that unwraps the response and the record lists. Adding a section used to mean finding
 * every one of those; the point of this file is that it now means adding an entry here.
 *
 * Nothing derived is authored. A collection's field-id prefix is `<section>.<collection>.`, and
 * writing it out again is how the two get to disagree.
 */

export const CASE_SECTION_ID = "case.case_details" as const;
export const PATIENT_SECTION_ID = "case.patient" as const;
export const PARENT_SECTION_ID = "case.parent" as const;
export const PARENT_MEDICAL_HISTORY_COLLECTION_ID = `${PARENT_SECTION_ID}.parent_medical_history` as const;
export const PARENT_PAST_DRUG_HISTORY_COLLECTION_ID = `${PARENT_SECTION_ID}.parent_past_drug_history` as const;
export const PAST_DRUG_HISTORY_COLLECTION_ID = `${PATIENT_SECTION_ID}.past_drug_history` as const;
export const TEST_PROCEDURES_COLLECTION_ID = `${PATIENT_SECTION_ID}.test_procedures` as const;
export const REPORTER_SECTION_ID = "case.reporter" as const;
export const PRODUCT_SECTION_ID = "case.product" as const;
export const EVENT_SECTION_ID = "case.event" as const;
export const ASSESSMENT_SECTION_ID = "case.assessment" as const;
export const NARRATIVE_SECTION_ID = "case.narrative" as const;

/** Sections whose text fields are long prose (the narrative), drawn full width and tall. */
export const LONG_TEXT_SECTION_IDS: ReadonlySet<string> = new Set([NARRATIVE_SECTION_ID]);
export const SUMMARY_SECTION_ID = "case.summary" as const;
export const REFERENCES_SECTION_ID = "case.references" as const;
export const REFERENCES_COLLECTION_ID = "references" as const;

export type CaseReviewSectionId =
  | typeof CASE_SECTION_ID
  | typeof PATIENT_SECTION_ID
  | typeof PARENT_SECTION_ID
  | typeof REPORTER_SECTION_ID
  | typeof PRODUCT_SECTION_ID
  | typeof EVENT_SECTION_ID
  | typeof ASSESSMENT_SECTION_ID
  | typeof NARRATIVE_SECTION_ID
  | typeof SUMMARY_SECTION_ID
  | typeof REFERENCES_SECTION_ID;

/** One repeatable list inside a section: how to find it, what to call it, how to title a card. */
export interface CollectionSpec {
  /** The property holding the rows, and the collection name the PATCH uses. */
  collectionId: string;
  /** The scope of the authored Control that carries the record layout in `options.detail`. */
  scope: string;
  labels: {
    records: string;
    empty: string;
    add: string;
    remove: string;
    /** Singular noun for a card with no title of its own: "Reporter 2". */
    item: string;
  };
  /**
   * A card's title from its own values, or undefined to fall back to `<item> <n>`.
   *
   * <p>`optionLabel` reads a coded field's label rather than its id, so a title can say
   * "50 mg, Once Daily" instead of "50 3 7".
   */
  title: (values: CaseFieldValues, optionLabel: (property: string) => string | undefined,
    recordId?: string) => string | undefined;
  /**
   * Extra tags on the card head, from the row's own values.
   *
   * <p>`optionLabel` reads a coded field's label rather than its id, because a tag saying "1" tells
   * a reviewer nothing; the labels are the ones the response served for that field.
   */
  tags?: (values: CaseFieldValues, optionLabel: (property: string) => string | undefined,
    recordId?: string) => string[];
  /** Pipeline-owned collections such as Assessment relationships cannot be created or removed here. */
  canAdd?: boolean;
  canRemove?: boolean;
  /**
   * Collections this collection's own records own.
   *
   * <p>One level: the compiled catalog has no deeper nesting, and the review PATCH says so in its
   * types. A child's `collectionId` is its dotted tail relative to the record holding it — the
   * same key the wire uses — and its `scope` is relative to the record's own layout.
   */
  children?: CollectionSpec[];
}

export interface CaseReviewSectionSpec {
  id: CaseReviewSectionId;
  icon: CaseReviewIconName;
  /** Used when the response carries no schema for the section; always scalar-projected before use. */
  fallbackSchema: JsonSchema;
  uischema: UISchemaElement;
  collections: CollectionSpec[];
  /**
   * The section's tabs are its one record's authored groups, with the record strip above them.
   *
   * <p>Products and Reporters are both nothing but one collection, so nothing derivable tells them
   * apart; the design does. Products shows its record's groups as tabs, Reporters keeps them as
   * collapsible rows inside one tab. That is a presentation choice, so the registry states it.
   */
  recordTabs?: boolean;
  collectionRenderer?: ComponentType<RecordTableProps>;
}

const MEDICAL_HISTORY: CollectionPresentation = {
  labels: {
    records: "Medical history records",
    empty: "No medical history recorded.",
    add: "Add medical history",
    remove: "Remove medical history",
    item: "Medical history",
  },
  title: (values) => typeof values.disease_term === "string" && values.disease_term.trim()
    ? values.disease_term : undefined,
};

const PAST_DRUG_HISTORY: CollectionPresentation = {
  labels: {
    records: "Past Drug History",
    empty: "No past drug history recorded.",
    add: "Add Past Drug",
    remove: "Remove Past Drug",
    item: "Past drug",
  },
  title: (values) => text(values.name),
  tags: (values) => [text(values.start_date), text(values.drug_reaction)]
    .filter((value): value is string => Boolean(value)),
};

const TEST_PROCEDURES: CollectionPresentation = {
  labels: {
    records: "Tests",
    empty: "No tests or procedures recorded.",
    add: "Add Test",
    remove: "Remove Test",
    item: "Test",
  },
  title: (values) => text(values.test_name),
  tags: (values) => [text(values.test_date), text(values.result)]
    .filter((value): value is string => Boolean(value)),
};

/**
 * A reporter is named the way a reviewer would read a list of them: family name first, because
 * that is what a list is scanned by. An organisation reported the case when no person is named,
 * so the institution answers "who reported this?" just as well; with neither, the card falls back
 * to its position, which is the only honest thing left to say about it.
 */
const REPORTERS: CollectionPresentation = {
  labels: {
    records: "Reporters",
    empty: "No reporters recorded.",
    add: "Add reporter",
    remove: "Remove reporter",
    item: "Reporter",
  },
  title: (values) => {
    const last = typeof values.last_name === "string" ? values.last_name.trim() : "";
    const first = typeof values.first_name === "string" ? values.first_name.trim() : "";
    if (last || first) return [last, first].filter(Boolean).join(", ");
    const institution = typeof values.institution_name === "string" ? values.institution_name.trim() : "";
    return institution || undefined;
  },
  /**
   * What this reporter is, in the words the design uses: "HCP · Primary reporter".
   *
   * <p>Both are facts a reviewer scans a list of reporters for — whether the source was a health
   * care professional, and which one the case is filed under — so they belong on the chip rather
   * than several rows into the record.
   */
  tags: (values) => [
    values.health_care_professional === true ? "HCP" : undefined,
    values.is_primary === true ? "Primary reporter" : undefined,
  ].filter((tag): tag is string => !!tag),
};

/** FDA.C.5.6.r: the other INDs the report also goes to, each named by its number. */
const CROSS_REPORTED_INDS: CollectionPresentation = {
  labels: {
    records: "Cross-reported INDs",
    empty: "No cross-reported IND recorded.",
    add: "Add cross-reported IND",
    remove: "Remove cross-reported IND",
    item: "Cross-reported IND",
  },
  title: (values) => text(values.ind_number),
};

/** The literature references of the case: named by the article title, else by the journal. */
const LITERATURE: CollectionPresentation = {
  labels: {
    records: "Literature",
    empty: "No literature reference recorded.",
    add: "Add literature reference",
    remove: "Remove literature reference",
    item: "Literature reference",
  },
  title: (values) => text(values.title) ?? text(values.journal),
  tags: (values) => [text(values.publication_date), text(values.digital_object_identifier)]
    .filter((value): value is string => Boolean(value)),
};

/** A text value, trimmed, or nothing — the shape every card title is built out of. */
function text(value: CaseFieldValues[string]): string | undefined {
  return typeof value === "string" && value.trim() ? value.trim() : undefined;
}

/**
 * What a product owns: four lists of its own and the entries of its quality-control record.
 *
 * <p>The entries' id carries a dot because the collection sits inside an object on the product
 * rather than directly on it — the same key the PATCH uses, and the same one the response nests
 * its review state under.
 */
const PRODUCT_PRESENTATION: Record<string, CollectionPresentation> = {
  "case.product.products.dosage_regimens": {
    labels: { records: "Dosage regimens", empty: "No dosage regimen recorded.",
      add: "Add regimen", remove: "Remove regimen", item: "Regimen" },
    // How much, how often: the two things that tell one regimen from another at a glance.
    title: (values, optionLabel) => [
      [values.dose, optionLabel("dose_unit")].filter(Boolean).join(" "),
      optionLabel("frequency"),
    ].filter(Boolean).join(", ") || undefined,
  },
  "case.product.products.substances": {
    labels: { records: "Substances", empty: "No substance recorded.",
      add: "Add substance", remove: "Remove substance", item: "Substance" },
    title: (values) => text(values.substance_name),
  },
  "case.product.products.indications": {
    labels: { records: "Indications", empty: "No indication recorded.",
      add: "Add indication", remove: "Remove indication", item: "Indication" },
    title: (values) => text(values.indication),
  },
  "case.product.products.parts": {
    labels: { records: "Name parts", empty: "No name part recorded.",
      add: "Add name part", remove: "Remove name part", item: "Name part" },
    title: (values) => text(values.name_part),
  },
  "case.product.products.quality_control.entries": {
    labels: { records: "Quality control entries", empty: "No quality control entry recorded.",
      add: "Add entry", remove: "Remove entry", item: "Entry" },
    title: (values) => text(values.lot_number),
  },
};

/**
 * A product is named the way the case names it, and tagged with what it was to the patient.
 *
 * <p>The role is the discriminator everything downstream keys on — the E2B block, the assessment,
 * the reader's three review sections — so it belongs on the card head rather than three rows down,
 * and it shows as its label because "1" says nothing.
 */
const PRODUCTS: CollectionPresentation = {
  labels: { records: "Products", empty: "No products recorded.",
    add: "Add product", remove: "Remove product", item: "Product" },
  title: (values) => text(values.product_name) ?? text(values.trade_name),
  tags: (values, optionLabel) => [
    optionLabel("product_role"),
    values.is_primary === true ? "Primary" : undefined,
  ].filter((tag): tag is string => !!tag),
};

const EVENTS: CollectionPresentation = {
  labels: { records: "Events", empty: "No events recorded.",
    add: "Add event", remove: "Remove event", item: "Event" },
  title: (values) => text(values.reported_term) ?? text(values.preferred_term),
  tags: (values) => [
    values.is_primary === true ? "Primary" : undefined,
    values.is_serious === true ? "Serious" : undefined,
  ].filter((tag): tag is string => !!tag),
};

const ASSESSMENT_PRESENTATION: Record<string, CollectionPresentation> = {
  "case.assessment.relationships": {
    labels: { records: "Event assessments", empty: "No event assessments recorded.",
      add: "Add assessment", remove: "Remove assessment", item: "Event assessment" },
    title: () => undefined,
  },
  "case.assessment.relationships.datasheets": {
    labels: { records: "Product datasheets", empty: "No product datasheets recorded.",
      add: "Add datasheet", remove: "Remove datasheet", item: "Datasheet" },
    title: (values) => text(values.trade_name) ?? text(values.datasheet_name),
  },
};

/**
 * A reference is scanned by the identifier the reporter gave it, and tagged with what kind of
 * document it is — the two columns the legacy panel put first, in that order.
 */
const REFERENCES: CollectionPresentation = {
  labels: {
    records: "References",
    empty: "No references recorded.",
    add: "Add reference",
    remove: "Remove reference",
    item: "Reference",
  },
  title: (values) => text(values.reference_id),
  tags: (_values, optionLabel) => [optionLabel("reference_type")]
    .filter((tag): tag is string => !!tag),
};

/**
 * A note is scanned by its category, and belongs to a follow-up.
 *
 * <p>The follow-up is the subtitle because that is how the legacy panel grouped these rows
 * (AttachedFilesPanel.tsx:30-48): absent or zero is the initial report, anything else is a
 * numbered follow-up. It reads the row's own value through the same path as every other tag
 * rather than reaching into the response.
 */
const NOTES: CollectionPresentation = {
  labels: {
    records: "Notes",
    empty: "No notes recorded.",
    add: "Add note",
    remove: "Remove note",
    item: "Note",
  },
  title: (values) => text(values.category),
  tags: (values) => [followupTag(values.followup_version)],
};

/** Absent and zero are the same thing here: a note filed against the initial report. */
function followupTag(value: CaseFieldValues[string]): string {
  const version = typeof value === "number" ? value : Number(value ?? 0);
  return Number.isFinite(version) && version > 0 ? followupVersionTag(version) : INITIAL_FOLLOWUP_TAG;
}

export const CASE_REVIEW_SECTIONS: CaseReviewSectionSpec[] = [
  {
    id: CASE_SECTION_ID,
    icon: "clipboard",
    fallbackSchema: caseSchema as JsonSchema,
    uischema: caseUiSchema as UISchemaElement,
    collections: catalogCollections(caseSchema as JsonSchema, caseUiSchema as UISchemaElement,
      { "case.case_details.case_external_identifiers": {
        labels: { records: "External case identifiers", empty: "No external case identifiers recorded.",
          add: "Add identifier", remove: "Remove identifier", item: "Identifier" },
        title: (values) => text(values.value),
        tags: (values) => [text(values.type), text(values.issuer)].filter((value): value is string => Boolean(value)),
      },
      "case.case_details.cross_reported_inds": CROSS_REPORTED_INDS,
      "case.case_details.literature": LITERATURE }),
  },
  {
    id: PATIENT_SECTION_ID,
    icon: "user",
    fallbackSchema: patientSchema as JsonSchema,
    uischema: hideDeferredReviewFields(patientUiSchema as UISchemaElement),
    collections: catalogCollections(patientSchema as JsonSchema, patientUiSchema as UISchemaElement,
      {
        "case.patient.medical_history": MEDICAL_HISTORY,
        [PAST_DRUG_HISTORY_COLLECTION_ID]: PAST_DRUG_HISTORY,
        [TEST_PROCEDURES_COLLECTION_ID]: TEST_PROCEDURES,
      }),
  },
  {
    id: PARENT_SECTION_ID,
    icon: "user",
    fallbackSchema: parentSchema as JsonSchema,
    uischema: parentUiSchema as UISchemaElement,
    collections: catalogCollections(parentSchema as JsonSchema, parentUiSchema as UISchemaElement, {
      [PARENT_MEDICAL_HISTORY_COLLECTION_ID]: {
        ...MEDICAL_HISTORY,
        labels: { ...MEDICAL_HISTORY.labels, add: "Add Medical History", remove: "Remove Medical History" },
      },
      [PARENT_PAST_DRUG_HISTORY_COLLECTION_ID]: {
        labels: { records: "Past drug history", empty: "No past drug history recorded.",
          add: "Add Past Drug", remove: "Remove Past Drug", item: "Past drug" },
        title: (values) => text(values.name_of_drug_as_reported),
      },
    }),
  },
  {
    // A section of its own, as it is on the wire and in CaseDataOCR's Reporter Information; the
    // mock folds it into Case, which Raja rejected for our adaptation (D64).
    id: REPORTER_SECTION_ID,
    icon: "file",
    fallbackSchema: reporterSchema as unknown as JsonSchema,
    uischema: reporterUiSchema as unknown as UISchemaElement,
    collections: catalogCollections(reporterSchema as unknown as JsonSchema, reporterUiSchema as unknown as UISchemaElement,
      { "case.reporter.reporters": REPORTERS }),
  },
  {
    id: PRODUCT_SECTION_ID,
    collectionRenderer: ProductRecordReview,
    icon: "box",
    fallbackSchema: productSchema as JsonSchema,
    uischema: hideDeferredReviewFields(productUiSchema as unknown as UISchemaElement),
    collections: catalogCollections(productSchema as JsonSchema, productUiSchema as unknown as UISchemaElement,
      { ...PRODUCT_PRESENTATION, "case.product.products": PRODUCTS })
      .map((collection) => ({ ...collection,
        children: collection.children?.filter((child) => child.collectionId !== "parts"),
      })),
    recordTabs: true,
  },
  {
    id: EVENT_SECTION_ID,
    icon: "clipboard",
    fallbackSchema: eventSchema as JsonSchema,
    uischema: eventUiSchema as unknown as UISchemaElement,
    collections: catalogCollections(eventSchema as JsonSchema, eventUiSchema as unknown as UISchemaElement,
      { "case.event.events": EVENTS }),
    recordTabs: true,
  },
  {
    id: ASSESSMENT_SECTION_ID,
    icon: "file",
    fallbackSchema: assessmentSchema as JsonSchema,
    uischema: assessmentUiSchema as unknown as UISchemaElement,
    collections: catalogCollections(assessmentSchema as JsonSchema, assessmentUiSchema as unknown as UISchemaElement,
      ASSESSMENT_PRESENTATION).map((collection) => ({ ...collection, canAdd: false, canRemove: false,
        children: collection.children?.map((child) => ({ ...child, canAdd: false, canRemove: false })) })),
    collectionRenderer: AssessmentRecordReview,
  },
  {
    // Narrative and its three comments: the AI's extracted description and the reviewer's own
    // narrative are separate fields, because the legacy screen conflated two different facts.
    id: NARRATIVE_SECTION_ID,
    icon: "panel",
    fallbackSchema: narrativeSchema as JsonSchema,
    uischema: narrativeUiSchema as unknown as UISchemaElement,
    collections: [],
  },
  {
    id: SUMMARY_SECTION_ID,
    icon: "clipboard",
    fallbackSchema: summarySchema as JsonSchema,
    uischema: summaryUiSchema as unknown as UISchemaElement,
    collections: [],
  },
  {
    // Two lists rather than one section each: both are per-case auxiliary records, and neither is
    // a clinical determination, so Case Summary stays scalar.
    id: REFERENCES_SECTION_ID,
    icon: "file",
    fallbackSchema: referencesSchema as JsonSchema,
    uischema: referencesUiSchema as unknown as UISchemaElement,
    collections: catalogCollections(referencesSchema as JsonSchema,
      referencesUiSchema as unknown as UISchemaElement,
      { "case.references.references": REFERENCES, "case.references.notes": NOTES }),
  },
];

export const CASE_REVIEW_SECTION_IDS: CaseReviewSectionId[] =
  CASE_REVIEW_SECTIONS.map((section) => section.id);

/** The one key a collection's draft, api and counts are all stored under. */
export function collectionKey(sectionId: string, collectionId: string): string {
  return `${sectionId}.${collectionId}`;
}

/** Every field id of a collection's rows starts with this; it is never authored. */
export function fieldIdPrefix(sectionId: string, collectionId: string): string {
  return `${collectionKey(sectionId, collectionId)}.`;
}

/** Every collection of every section, each with the section it belongs to. */
export function caseReviewCollections(): Array<{ sectionId: CaseReviewSectionId; collection: CollectionSpec }> {
  return CASE_REVIEW_SECTIONS.flatMap((section) =>
    section.collections.map((collection) => ({ sectionId: section.id, collection })));
}
